import { inject, Injectable } from '@angular/core';
import { AuthService } from '../../../app/services/auth.service';
import { AWSLambda } from '../../AWSLambda.service';
import { Auth } from 'aws-amplify';
import { DatePipe } from '@angular/common';
import { CognitoUserAttribute } from 'amazon-cognito-identity-js';

/**
 * Representa una tarjeta registrada en Paylands.
 */
interface Card {
  id: string;
  last4: string;
  card_brand: string;
  bin: string;
  bank_name: string;
}

/**
 * Información de configuración y tarjetas disponibles para un usuario en Paylands.
 */
interface Paylands {
  allow_direct: boolean;
  cards: Card[];
}

/**
 * Servicio para manejar operaciones de transferencias, incluyendo integración con AWS Lambda,
 * gestión de usuarios Revolut, cotizaciones y URLs de pago.
 */
@Injectable({
  providedIn: 'root'
})
export class TransfersService {

  /** Instancia del servicio AWSLambda para llamar funciones Lambda. */
  lambda = inject(AWSLambda);

  /** Instancia del servicio de autenticación. */
  authService = inject(AuthService);

  /** Utilidad para formatear fechas. */
  private datePipe = inject(DatePipe);

  /**
   * Inicializa el servicio refrescando la sesión del usuario autenticado.
   */
  async init(): Promise<void> {
    await this.authService.refresh();
  }

  /**
   * Crea o actualiza un usuario Revolut con sus atributos y país de residencia.
   * 
   * @param revolutID - ID único del usuario Revolut.
   * @param attributes - Atributos de usuario desde Cognito.
   * @param email - Email del usuario.
   * @param countrycode - Información del país del usuario.
   * @returns La respuesta de la Lambda o `null` en caso de error.
   */
  async createOrUpdateRevoluUser(
    revolutID: string | null,
    attributes: any,
    email: string,
    countrycode: { countryCode: string, country: string, iso3: string } | null
  ): Promise<any> {
    try {
      if (attributes && typeof attributes === 'object' && Object.keys(attributes).length > 0) {
        const response = await this.lambda.getLambdaFunction('lambda', '/revolu/api', {
          queryStringParameters: {
            method: 'upsertUser',
            userId: revolutID,
            email: email,
            country: countrycode?.iso3 ?? 'ESP',
            sub: attributes.sub ?? '',
            firstName: attributes.name ?? '',
            lastName: attributes.middle_name ?? '',
            phone: attributes.phone_number ?? '',
            address: attributes?.['custom:address'] ?? '',
            city: attributes?.['custom:ciudad'] ?? '',
            state: attributes?.['custom:provincia'] ?? '',
            postalCode: attributes?.['custom:postcode'] ?? '',
            birthDate: attributes?.['custom:birthdate'] ?? '1970-01-01',
          }
        });
        return response;
      }
    } catch (error) {
      console.error('Error calling Lambda function:', error);
      return null;
    }
    return null;
  }

  /**
   * Obtiene información de tarjetas y configuración de Paylands para el usuario.
   *
   * @param revolutID - ID del usuario Revolut.
   * @returns Objeto `Paylands` con las tarjetas o configuración por defecto si falla.
   */
  async getPaylands(revolutID: string): Promise<Paylands> {
    try {
      const response = await this.lambda.getLambdaFunction('lambda', '/revolu/api', {
        queryStringParameters: {
          method: "getPaylandsCards",
          userId: revolutID
        }
      });

      return response[0];
    } catch (error) {
      console.error('Error calling Lambda function:', error);
    }
    return { allow_direct: false, cards: [] };
  }

  /**
   * Obtiene una cotización para una transferencia según el producto, monto y moneda.
   * 
   * @param productid - ID del producto.
   * @param amount - Monto a enviar.
   * @param currency - Moneda del envío (por defecto "EUR").
   * @returns Objeto con información de la cotización o `null` si hay error.
   */
  async getQuotation(
    productid: string,
    amount: number,
    currency: string = "EUR"
  ): Promise<{
    quotation_id: number;
    send_amount: number;
    fee: number;
    revenue: number;
    total_due: number;
    exchange_rate: number;
    receive_currency: string;
    receive_amount: number;
    differs_from_requested_amount: boolean;
    direct_banking_revenue: number;
    card_revenue: number;
  } | null | undefined> {
    try {
      const response = await this.lambda.getLambdaFunction('lambda', '/revolu/api', {
        queryStringParameters: {
          method: 'newQuotation',
          productId: productid,
          sendCurrency: currency,
          mode: 'SEND',
          amount: amount
        }
      });

      return response;
    } catch (error) {
      console.error('Error calling Lambda function:', error);
    }
    return null;
  }

  /**
   * Crea una transacción y genera una URL de pago (Paylands o banca directa).
   * 
   * @param attributes - Atributos del usuario.
   * @param finalData - Datos de la transacción.
   * @param payment_method - Método de pago seleccionado.
   * @param cardId - ID de tarjeta guardada (si aplica).
   * @param save_card - Si se debe guardar la tarjeta.
   * @param country - País de destino.
   * @returns Objeto con `payment_url` o `false` si falla.
   */
  async getPaymentURL(
    attributes: any,
    finalData: {},
    payment_method: string,
    cardId: string,
    save_card: number,
    country: string
  ): Promise<any> {
    try {
      const transaction = await this.lambda.getLambdaFunction('lambda', '/revolu/api', {
        queryStringParameters: {
          method: "newTransaction",
          country: country,
          sub: attributes?.sub ?? '',
          ...serializeQueryParams(finalData)
        }
      });

      if (transaction) {
        const response = await this.lambda.getLambdaFunction('lambda', '/revolu/api', {
          queryStringParameters: {
            method: payment_method === "PAYMENT_CARD" ? "getPaylandsUrl" : "getDirectBankingUrl",
            transactionId: transaction.transaction_id,
            cardId: payment_method === "PAYMENT_CARD" && cardId ? cardId : null,
            saveCard: payment_method === "PAYMENT_CARD" ? save_card : null
          }
        });

        window.open(response.payment_url, '_self');
        return response;
      }
    } catch (error) {
      console.error('Error calling Lambda function:', error);
      return false;
    }
  }
}

/**
 * Convierte un objeto plano a un objeto de parámetros de cadena de consulta.
 * Si algún valor es un objeto, lo serializa como JSON.
 *
 * @param data - Objeto con los datos a convertir.
 * @returns Objeto plano de strings.
 */
function serializeQueryParams(data: any): Record<string, string> {
  const result: Record<string, string> = {};
  for (const key in data) {
    const value = data[key];
    result[key] = typeof value === 'object' ? JSON.stringify(value) : String(value);
  }
  return result;
}
